/**
 * ForwardWeeklyPanel — "What it adds up to", for whichever instrument is being typed.
 *
 * THE BLOCK IS THE CONSEQUENCE, STATED BEFORE IT IS COMMITTED, and it is the same consequence on
 * every dossier that raises a weekly recovery. Typing 200 into an advance's repayment area and
 * typing 200 into an insurance premium advance's recovery area both raise what the driver owes from
 * the Monday of the first repayment's week, and both are composed the same way — the latest contract
 * instalment, every insurance premium advance's current rate, every other standing advance's current
 * rate, and the figure being typed. `weekly_rate` is deliberately not read for it; the argument for
 * that is `advanceArithmetic.ts`'s, and this panel is its render.
 *
 * WHICH COHORT THE SUBJECT CAME OUT OF IS THE ONLY THING THAT DIFFERS, WHICH IS WHY TWO LABELS ARE
 * PROPS. The caller passes the OTHER advances — the one being typed into is never among them — so on
 * the advance dossier the two Σ rows are every insurance premium advance and every other advance,
 * while on the insurance dossier this policy's own loan has been taken out of the first of them and
 * the row that says "Insurance premium advances" would be naming a sum that excludes the very loan
 * the admin is looking at. The figures are identical; the words over them are not.
 *
 * AND THE TERM ROW IS THE HOST'S, WHICH IS WHY IT ARRIVES AS `children`. The advance's Schedule
 * prints a term over the total repayable; the insurance dossier's term is derived net of the
 * driver's own payment [D-B46] and belongs beside the figures it is net of, on that canvas's
 * schedule block. A panel that derived one for both would have to be handed a total that means two
 * different things, so it is handed a row instead — and a host with no term to state passes none.
 */

import type { ReactNode } from 'react';

import { formatCurrency } from '@/lib/format';

import { Block, Hint, Rec, Rows } from '../../areas/blocks';
import { forwardWeeklyTotal, type WeeklyInstalmentRow } from '../advanceArithmetic';

interface Props {
    /** The forward sum's OTHER terms — the instrument being typed is never among them. */
    forward: {
        contractInstalment: number | null;
        insuranceLoanInstalments: WeeklyInstalmentRow[];
        loanInstalments: WeeklyInstalmentRow[];
    };
    /** The weekly figure being typed, already parsed. Null while the field is empty. */
    typed: number | null;
    /** What the Σ of the insurance premium advances is called on this canvas. */
    insuranceLoansLabel: string;
    /** And what the Σ of every other standing advance is called. */
    loansLabel: string;
    /** The Expected term row, for a host that states one inside this block. */
    children?: ReactNode;
}

function money(value: number | null): string | null {
    return value === null || value === undefined ? null : formatCurrency(value);
}

export default function ForwardWeeklyPanel({
    forward,
    typed,
    insuranceLoansLabel,
    loansLabel,
    children,
}: Props) {
    const weekly = forwardWeeklyTotal(
        forward.contractInstalment,
        forward.insuranceLoanInstalments,
        forward.loanInstalments,
        typed,
    );

    return (
        <Block id="b-adds-up" title="What it adds up to" aside="never printed">
            <p className="sd-lede sd-dim">
                Composed here from the contract’s own instalment and every standing advance on it —
                not read back from the weekly figure the app shows, which states what the driver was
                billed at their last repayment.
            </p>
            <Rows>
                <Rec label="Contract instalment" value={money(weekly.contract)} empty="Not recorded" />
                {/* THE TWO Σ ROWS ARE SUMS AND NEVER NULLS. `currentWeekly` is a reduce seeded
                    at 0, so a contract carrying no insurance advance and no other advance would
                    draw GH₵0.00 on both rows and the "None" fallback could never render —
                    unlike the two rows either side, whose nulls are real. Nought here means
                    there is nothing standing, and the row says so. */}
                <Rec
                    label={insuranceLoansLabel}
                    value={weekly.insuranceLoans === 0 ? null : money(weekly.insuranceLoans)}
                    empty="None"
                />
                <Rec
                    label={loansLabel}
                    value={weekly.loans === 0 ? null : money(weekly.loans)}
                    empty="None"
                />
                <Rec
                    label="This advance"
                    value={money(weekly.typed)}
                    empty="Not set"
                />
                <Rec
                    label="Weekly total from the first repayment"
                    value={money(weekly.total)}
                    sub={
                        weekly.before === null
                            ? undefined
                            : `today ${formatCurrency(weekly.before)}`
                    }
                    empty="No contract instalment recorded"
                />
                {children}
            </Rows>
            <Hint className="mt-3">
                The overdue engine expects the raised total from the Monday of the first
                repayment’s week. The weekly figure on the contract’s header re-derives from the
                driver’s last repayment, so it catches up at the next payment rather than the
                moment this is saved — the two disagreeing in between is the design, not a
                failed save.
            </Hint>
        </Block>
    );
}
